<template>
  <div
    class="workshop-checklist"
    :class="{
      'workshop-checklist-desktop': desktop && view,
      'workshop-checklist-phone': !desktop && view,
    }"
  >
    <div class="workshop-top pa-3 pa-md-4 pb-md-2">
      <router-link
        :to="{ name: 'workshop' }"
        class="d-none d-md-inline-flex align-center text-caption mb-1"
      >
        <v-icon size="14">mdi-arrow-left</v-icon>
        {{ $t("workshop.checklist.back") }}
      </router-link>

      <!-- Loading -->
      <v-skeleton-loader v-if="loadState === 'loading'" type="heading, text" />

      <!-- Not found (404, another institution's, or the module off) -->
      <v-card
        v-else-if="loadState === 'not_found'"
        variant="outlined"
        class="pa-8 text-center"
      >
        <v-icon size="48" color="grey-darken-1">
          mdi-clipboard-remove-outline
        </v-icon>
        <div class="text-h6 mt-3">
          {{ $t("workshop.checklist.not_found_title") }}
        </div>
        <div class="text-body-2 text-medium-emphasis mt-1 mb-4">
          {{ $t("workshop.checklist.not_found_body") }}
        </div>
        <v-btn color="primary" variant="flat" :to="{ name: 'workshop' }">
          {{ $t("workshop.checklist.not_found_back") }}
        </v-btn>
      </v-card>

      <v-alert v-else-if="loadState === 'error'" type="error" variant="tonal">
        <div class="d-flex flex-wrap align-center ga-2">
          <span class="flex-grow-1">
            {{ $t("workshop.checklist.load_error") }}
          </span>
          <v-btn variant="outlined" size="small" @click="load">
            {{ $t("workshop.checklist.retry") }}
          </v-btn>
        </div>
      </v-alert>

      <template v-else-if="view">
        <!-- Header -->
        <div class="d-flex flex-wrap align-center ga-2">
          <!-- No plate: the VIN takes its place. -->
          <h1
            class="text-h6 text-md-h5 font-weight-bold mr-1"
            :class="{ 'workshop-vin': !plate && vin }"
          >
            {{ plate || vin || "—" }}
          </h1>
          <span v-if="plate && vin" class="text-body-2 workshop-vin">
            {{ $t("workshop.home.columns.vin") }}: {{ vin }}
          </span>
          <span v-if="vehicleName" class="text-body-1 workshop-wrap">
            {{ vehicleName }}
          </span>
          <v-chip v-if="chip" size="small" variant="tonal" :color="chip.color">
            {{ $t(chip.key) }}
          </v-chip>
          <span class="text-caption text-medium-emphasis">
            {{
              $t("acceptance.depollution.template_version", {
                version: view.checklist.template.version,
              })
            }}
          </span>
          <v-chip
            v-if="deadline"
            size="small"
            variant="tonal"
            :color="deadline.color"
          >
            {{ $t(deadline.key, deadline.params) }}
          </v-chip>
          <v-btn
            class="d-none d-md-flex ml-auto"
            size="small"
            variant="outlined"
            color="primary"
            prepend-icon="mdi-clipboard-text-outline"
            :to="{
              name: 'acceptance',
              params: { dossierId: view.checklist.dossierId },
            }"
          >
            {{ $t("workshop.checklist.open_acceptance") }}
          </v-btn>
        </div>

        <div class="text-body-2 mt-1 workshop-wrap">
          {{
            $t("acceptance.depollution.progress", {
              done: count.done,
              total: count.total,
            })
          }}
          <template v-if="view.state !== 'approved'">
            <template v-if="count.currentTaskCode">
              ·
              {{
                $t("acceptance.depollution.current_task", {
                  task: $t(taskKey(count.currentTaskCode)),
                })
              }}
            </template>
            ·
            {{ $t("workshop.checklist.remaining", { count: count.remaining }) }}
          </template>
        </div>
        <v-progress-linear
          class="d-none d-md-flex mt-2"
          :model-value="count.percent"
          height="8"
          rounded
          :color="view.state === 'approved' ? 'green' : 'primary'"
        />

        <!-- Banners -->
        <!-- Approved (M5): who, when, template, due date and timing; corrections only. -->
        <v-alert
          v-if="banner"
          type="success"
          variant="tonal"
          density="compact"
          class="mt-3 workshop-wrap"
        >
          {{
            $t(
              banner.dueDate
                ? "workshop.checklist.approved_banner"
                : "workshop.checklist.approved_banner_no_due",
              {
                name: banner.name,
                date: banner.date,
                version: banner.version,
                due: banner.dueDate,
                timing: $t(`workshop.checklist.timing.${banner.timing}`),
              },
            )
          }}
        </v-alert>
        <v-alert
          v-if="sessionState.conflict || writeConflict"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          <div class="d-flex flex-wrap align-center">
            <div class="mr-auto py-1">
              <strong>{{ $t("acceptance.conflict.title") }}</strong>
              {{ $t("acceptance.conflict.body") }}
            </div>
            <v-btn color="primary" variant="flat" @click="load">
              {{ $t("acceptance.conflict.reload") }}
            </v-btn>
          </div>
        </v-alert>
        <v-alert
          v-if="quarantine"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ $t("acceptance.depollution.quarantine") }}
        </v-alert>
        <v-alert
          v-if="overdue"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ $t("workshop.checklist.overdue_banner", { date: dueDate }) }}
        </v-alert>
        <v-alert
          v-if="estimated.length"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ $t("workshop.checklist.estimated_banner") }}
          <a
            v-for="code in estimated"
            :key="code"
            href="#"
            class="workshop-link ml-2"
            @click.prevent="select(code)"
          >
            {{ $t(taskKey(code)) }}
          </a>
        </v-alert>
      </template>
    </div>

    <!-- Approved, desktop: the summary list and the selected task's read-only detail side by
         side, each column scrolling on its own. -->
    <div v-if="view && approved && desktop" class="workshop-columns px-4 pb-4">
      <div class="workshop-column workshop-column-summary">
        <workshop-approved-summary
          :view="view"
          :selected="approvedCode"
          @open="openDetail"
        />
      </div>
      <div class="workshop-column-main">
        <div v-if="approvedTask" class="workshop-column">
          <workshop-task-card
            :task="approvedTask"
            :task-count="view.tasks.length"
            :current-task-code="null"
            :editable="false"
            :arrival-date="view.arrivalDate"
            :files="view.files"
            :blobs="blobs"
            :can-correct="view.canCorrect"
            :waste-enabled="wasteEnabled"
            @select="openDetail"
            @correct="openCorrection"
          />
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="mt-3 workshop-wrap"
          >
            {{ $t("workshop.checklist.approved_footer") }}
          </v-alert>
        </div>
      </div>
    </div>

    <!-- Approved, phone: the summary list, or one task's read-only detail. -->
    <template v-else-if="view && approved">
      <div class="workshop-approved px-3 pb-3">
        <workshop-approved-summary
          v-if="!detailTask"
          :view="view"
          @open="openDetail"
        />
        <template v-else>
          <workshop-task-card
            :task="detailTask"
            :task-count="view.tasks.length"
            :current-task-code="null"
            :editable="false"
            :arrival-date="view.arrivalDate"
            :files="view.files"
            :blobs="blobs"
            :can-correct="view.canCorrect"
            :waste-enabled="wasteEnabled"
            @select="openDetail"
            @correct="openCorrection"
          />
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="mt-3 workshop-wrap"
          >
            {{ $t("workshop.checklist.approved_footer") }}
          </v-alert>
        </template>
      </div>

      <div v-if="detailTask" class="workshop-footer">
        <div class="workshop-bottom-bar">
          <v-btn
            variant="text"
            prepend-icon="mdi-chevron-left"
            :disabled="!detailNeighbours.previous"
            @click="openDetail(detailNeighbours.previous)"
          >
            {{ $t("workshop.checklist.previous") }}
          </v-btn>
          <v-btn
            variant="tonal"
            color="primary"
            prepend-icon="mdi-format-list-bulleted"
            @click="detail = null"
          >
            {{ $t("workshop.checklist.summary") }}
          </v-btn>
          <v-btn
            variant="text"
            append-icon="mdi-chevron-right"
            :disabled="!detailNeighbours.next"
            @click="openDetail(detailNeighbours.next)"
          >
            {{ $t("workshop.checklist.next") }}
          </v-btn>
        </div>
      </div>
    </template>

    <template v-else-if="view && selectedTask">
      <!-- Desktop: task list and selected task, each column scrolling on its own. -->
      <div v-if="desktop" class="workshop-columns px-4 pb-4">
        <div class="workshop-column workshop-column-list">
          <v-list density="compact" class="py-0">
            <template v-for="group in groups" :key="group.group">
              <v-list-subheader>{{ $t(group.groupKey) }}</v-list-subheader>
              <v-list-item
                v-for="task in group.tasks"
                :key="task.code"
                :active="task.code === selected"
                color="primary"
                @click="select(task.code)"
              >
                <v-list-item-title class="workshop-wrap text-wrap">
                  {{ $t(taskKey(task.code)) }}
                  <span
                    v-if="!task.mandatory"
                    class="text-caption text-medium-emphasis"
                  >
                    {{ $t("workshop.checklist.optional") }}
                  </span>
                </v-list-item-title>
                <template #append>
                  <v-chip
                    size="x-small"
                    variant="tonal"
                    :color="statusOf(task).color"
                  >
                    {{ $t(statusOf(task).key) }}
                  </v-chip>
                </template>
              </v-list-item>
            </template>
          </v-list>
        </div>

        <div class="workshop-column-main">
          <div class="workshop-column">
            <workshop-task-card
              :task="selectedTask"
              :task-count="view.tasks.length"
              :current-task-code="count.currentTaskCode"
              :editable="editable"
              :arrival-date="view.arrivalDate"
              :saving="resultSaving"
              :server-error="cardError"
              :files="view.files"
              :blobs="blobs"
              :can-accept="panel.canAccept"
              :can-correct="view.canCorrect"
              :waste-enabled="wasteEnabled"
              @select="select"
              @save="saveResult"
              @accept="openAccept"
              @correct="openCorrection"
            />
          </div>

          <v-card variant="outlined" class="mt-3 workshop-approval">
            <workshop-approval-panel
              :view="view"
              :approving="decision.saving && decision.mode === 'approve'"
              @select="select"
              @approve="openApprove"
            />
          </v-card>
        </div>
      </div>

      <!-- Phone: one task per screen. -->
      <template v-else>
        <div class="px-3 pb-3">
          <workshop-task-card
            :task="selectedTask"
            :task-count="view.tasks.length"
            :current-task-code="count.currentTaskCode"
            :editable="editable"
            :arrival-date="view.arrivalDate"
            :saving="resultSaving"
            :server-error="cardError"
            :files="view.files"
            :blobs="blobs"
            :can-accept="panel.canAccept"
            :can-correct="view.canCorrect"
            :waste-enabled="wasteEnabled"
            @select="select"
            @save="saveResult"
            @accept="openAccept"
            @correct="openCorrection"
          />
        </div>

        <!-- Sticky footer: the collapsible approval panel above the bottom bar. -->
        <div class="workshop-footer">
          <workshop-approval-panel
            compact
            class="workshop-footer-approval px-3 py-2"
            :view="view"
            :approving="decision.saving && decision.mode === 'approve'"
            @select="select"
            @approve="openApprove"
          />
          <div class="workshop-bottom-bar">
            <v-btn
              variant="text"
              prepend-icon="mdi-chevron-left"
              :disabled="!neighbours.previous"
              @click="select(neighbours.previous)"
            >
              {{ $t("workshop.checklist.previous") }}
            </v-btn>
            <v-btn
              variant="tonal"
              color="primary"
              prepend-icon="mdi-format-list-bulleted"
              @click="sheet = true"
            >
              {{ $t("workshop.checklist.task_list") }}
            </v-btn>
            <v-btn
              variant="text"
              append-icon="mdi-chevron-right"
              :disabled="!neighbours.next"
              @click="select(neighbours.next)"
            >
              {{ $t("workshop.checklist.next") }}
            </v-btn>
          </div>
        </div>

        <v-bottom-sheet v-model="sheet" scrollable>
          <v-card>
            <v-card-title class="text-wrap">
              {{ $t("workshop.checklist.task_list") }}
            </v-card-title>
            <v-card-text class="pa-0">
              <v-list density="compact" class="py-0">
                <template v-for="group in groups" :key="group.group">
                  <v-list-subheader>{{ $t(group.groupKey) }}</v-list-subheader>
                  <v-list-item
                    v-for="task in group.tasks"
                    :key="task.code"
                    :active="task.code === selected"
                    color="primary"
                    min-height="48"
                    @click="pick(task.code)"
                  >
                    <v-list-item-title class="workshop-wrap text-wrap">
                      {{ $t(taskKey(task.code)) }}
                      <span
                        v-if="!task.mandatory"
                        class="text-caption text-medium-emphasis"
                      >
                        {{ $t("workshop.checklist.optional") }}
                      </span>
                    </v-list-item-title>
                    <template #append>
                      <v-chip
                        size="x-small"
                        variant="tonal"
                        :color="statusOf(task).color"
                      >
                        {{ $t(statusOf(task).key) }}
                      </v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-list>
            </v-card-text>
          </v-card>
        </v-bottom-sheet>
      </template>
    </template>

    <workshop-decision-dialog
      v-model="decision.open"
      :mode="decision.mode"
      :task-label="decision.taskCode ? $t(taskKey(decision.taskCode)) : ''"
      :reason="decision.reason"
      :timing="timing"
      :saving="decision.saving"
      :error="decision.error"
      @confirm="decide"
    />

    <!-- Slice 9: a correction of one recorded value (Manager/Admin), previewed with a dry run. -->
    <correction-dialog
      v-model="correction.open"
      :group="correction.field"
      :values="correctionValues"
      :fieldset="correctionFieldset"
      :saving="correction.saving"
      :error="correction.error"
      :preview="correction.preview"
      @submit="submitCorrection"
      @preview="previewCorrection"
      @update:model-value="onCorrectionToggle"
    />
  </div>
</template>

<script>
// Workshop task screen /workshop/:checklistId (Stage 3a plan §D2): header, banners, one task per
// screen with a bottom bar and a task sheet on the phone, two columns with the approval panel on
// desktop, and recording a result with evidence files (slices 5-6). The route needs the workshop switch (router meta
// requiresWorkshop); the API checks it again. The checklist has its own session (L20), so its
// revision never mixes with the dossier's. Slice 8: "Accetta eccezione" and "Approva bonifica"
// (confirm dialog; the phone has the panel above the bottom bar), and once approved the summary
// list with the read-only detail: side by side on desktop (Nike, 30 Oct 2026, overriding plan
// §D2), one after the other with "Precedente · Riepilogo · Successiva" on the phone. Slice 9: a
// Manager/Admin corrects a recorded value from the task card (before and after approval) in
// correctionDialog.vue with the workshop fieldset and a dry-run preview.
import {
  acceptWorkshopException,
  approveWorkshopChecklist,
  correctWorkshopResult,
  getFileBlob,
  getWorkshopChecklist,
  newCommandId,
  recordWorkshopResult,
} from "@/api/compliance";
import CorrectionDialog from "@/components/Acceptance/correctionDialog.vue";
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import { createBlobCache } from "@/components/Acceptance/dossierFiles.mjs";
import {
  createDossierSession,
  isConflictError,
} from "@/components/Acceptance/dossierSession.mjs";
import WorkshopApprovalPanel from "@/components/Workshop/workshopApprovalPanel.vue";
import WorkshopApprovedSummary from "@/components/Workshop/workshopApprovedSummary.vue";
import WorkshopDecisionDialog from "@/components/Workshop/workshopDecisionDialog.vue";
import WorkshopTaskCard from "@/components/Workshop/workshopTaskCard.vue";
import {
  correctionMessage,
  correctionSubmitKey,
  correctionValues as valuesOf,
  workshopFieldset,
} from "@/components/Workshop/workshopCorrections.mjs";
import {
  approvalTiming,
  decisionError,
  decisionKey,
} from "@/components/Workshop/workshopDecisions.mjs";
import {
  isWriteConflict,
  resultServerError,
  resultSubmitKey,
} from "@/components/Workshop/workshopForm.mjs";
import {
  approvalBanner,
  approvalPanel,
  checklistLoadState,
  counters,
  deadlineChip,
  groupedTasks,
  hasWarning,
  keepSelection,
  neighbourTasks,
  stateChip,
  taskKey,
  taskStatus,
} from "@/components/Workshop/workshopView.mjs";

export default {
  components: {
    CorrectionDialog,
    WorkshopApprovalPanel,
    WorkshopApprovedSummary,
    WorkshopDecisionDialog,
    WorkshopTaskCard,
  },
  data: () => ({
    // "loading" | "not_found" | "error" | "checklist"
    loadState: "loading",
    view: null,
    selected: null,
    sheet: false,
    sessionState: { revision: 0, busy: false, conflict: null },
    resultSaving: false,
    // A failed save: { taskCode, field, code, text }
    resultError: null,
    // A 409 that is not a revision conflict (result_exists, stale_result): the same banner.
    writeConflict: false,
    // Approved: the task whose read-only detail is open, or null for the summary list.
    detail: null,
    // The accept / approve confirm dialog. mode "accept" | "approve"; error is an i18n key.
    decision: {
      open: false,
      mode: "accept",
      resultId: null,
      taskCode: null,
      reason: "",
      saving: false,
      error: "",
    },
    // Slice 9: the correction of one value of a current result (Manager/Admin). preview is the
    // dry run { loading, error, data }.
    correction: {
      open: false,
      field: null,
      taskCode: null,
      resultId: null,
      saving: false,
      error: "",
      preview: null,
    },
  }),
  computed: {
    // Stage 6: fluids and batteries fill a waste container (the waste switch and the workshop).
    wasteEnabled() {
      return this.$store.getters.wasteEnabled === true;
    },
    // The only structure switch by width (plan §D): two columns from md up.
    desktop() {
      return this.$vuetify.display.mdAndUp;
    },
    plate() {
      return (this.view && this.view.car && this.view.car.targa) || "";
    },
    vin() {
      return (this.view && this.view.car && this.view.car.vin) || "";
    },
    vehicleName() {
      const car = this.view && this.view.car;
      return car ? [car.brand, car.model].filter(Boolean).join(" ") : "";
    },
    chip() {
      return this.view ? stateChip(this.view.state) : null;
    },
    deadline() {
      return this.view ? deadlineChip(this.view.deadline) : null;
    },
    dueDate() {
      return formatCivilDate(this.view && this.view.deadline.dueDate);
    },
    count() {
      return counters(this.view);
    },
    quarantine() {
      return hasWarning(this.view, "quarantine");
    },
    overdue() {
      return hasWarning(this.view, "overdue");
    },
    estimated() {
      const warning = ((this.view && this.view.warnings) || []).find(
        (item) => item && item.code === "estimated_quantity",
      );
      return (warning && warning.taskCodes) || [];
    },
    groups() {
      return groupedTasks(this.view);
    },
    selectedTask() {
      const tasks = (this.view && this.view.tasks) || [];
      return tasks.find((task) => task.code === this.selected) || null;
    },
    neighbours() {
      return neighbourTasks(this.view, this.selected);
    },
    panel() {
      return approvalPanel(this.view);
    },
    approved() {
      return Boolean(this.view && this.view.approval);
    },
    banner() {
      return approvalBanner(this.view);
    },
    timing() {
      return approvalTiming(this.view);
    },
    detailTask() {
      const tasks = (this.view && this.view.tasks) || [];
      return tasks.find((task) => task.code === this.detail) || null;
    },
    detailNeighbours() {
      return neighbourTasks(this.view, this.detail);
    },
    // Approved, desktop: the task in the right column, the first task until a row is picked.
    approvedCode() {
      const tasks = (this.view && this.view.tasks) || [];
      if (this.detailTask) return this.detail;
      return tasks.length ? tasks[0].code : null;
    },
    approvedTask() {
      const tasks = (this.view && this.view.tasks) || [];
      return tasks.find((task) => task.code === this.approvedCode) || null;
    },
    // Results can be recorded until the checklist is approved.
    editable() {
      return Boolean(this.view && !this.view.approval);
    },
    // The result being corrected, while it is still the task's current result.
    correctionResult() {
      const { taskCode, resultId } = this.correction;
      const tasks = (this.view && this.view.tasks) || [];
      const task = tasks.find((item) => item.code === taskCode);
      return task && task.current && task.current.id === resultId
        ? { task, result: task.current }
        : null;
    },
    correctionValues() {
      const found = this.correctionResult;
      return found
        ? valuesOf(found.task, found.result, {
            wasteEnabled: this.wasteEnabled,
          })
        : {};
    },
    correctionFieldset() {
      const found = this.correctionResult;
      if (!found) return null;
      return workshopFieldset(found.task, found.result, {
        arrivalDate: this.view.arrivalDate,
        locale: this.$i18n.locale,
        wasteEnabled: this.wasteEnabled,
      });
    },
    // The failed save's error, only on the task it belongs to.
    cardError() {
      const error = this.resultError;
      return error && error.taskCode === this.selected ? error : null;
    },
  },
  watch: {
    "$route.params.checklistId"(value, old) {
      if (value && value !== old) {
        this.selected = null;
        this.detail = null;
        this.blobs.revokeAll();
        this.load();
      }
    },
  },
  created() {
    // Not reactive on purpose: the page mirrors its state in sessionState via onChange.
    this.session = null;
    // Only the latest load may fill the page.
    this.loadSeq = 0;
    // { key, commandId } of the result being saved: a retry of the same input reuses it.
    this.pendingResult = null;
    // The same for an acceptance or the approval (workshopDecisions.decisionKey).
    this.pendingDecision = null;
    // The same for a correction (workshopCorrections.correctionSubmitKey).
    this.pendingCorrection = null;
    // Only the latest correction dry run may fill the preview.
    this.previewSeq = 0;
    // Object URLs of the evidence files, revoked on leave and when another checklist loads.
    this.blobs = createBlobCache({
      load: getFileBlob,
      revoke: (url) => URL.revokeObjectURL(url),
    });
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.blobs.revokeAll();
  },
  methods: {
    taskKey,
    async load() {
      const seq = (this.loadSeq += 1);
      this.loadState = "loading";
      try {
        const view = await getWorkshopChecklist(this.$route.params.checklistId);
        if (seq !== this.loadSeq) return;
        const revision = view.checklist.revision;
        this.view = view;
        this.selected = keepSelection(view, this.selected);
        this.loadState = "checklist";
        this.session = createDossierSession({
          revision,
          onChange: (state) => {
            this.sessionState = state;
          },
        });
        this.sessionState = { revision, busy: false, conflict: null };
        this.writeConflict = false;
        this.resultError = null;
        this.pendingResult = null;
        this.pendingDecision = null;
        this.pendingCorrection = null;
        if (!view.approval) this.detail = null;
      } catch (err) {
        if (seq !== this.loadSeq) return;
        this.view = null;
        this.loadState = checklistLoadState(err);
      }
    },
    statusOf(task) {
      return taskStatus(task, this.count.currentTaskCode);
    },
    select(code) {
      if (!code) return;
      this.selected = code;
    },
    // A task picked from the bottom sheet.
    pick(code) {
      this.select(code);
      this.sheet = false;
    },
    // Records the selected task's result (or its redo, with replacesResultId) through the
    // checklist session. The returned view updates the counter, the chips, the hint and the
    // approval panel; the task stays selected.
    // A 409 shows the conflict banner and a 422 the field error; the card keeps the input.
    async saveResult(payload) {
      if (!this.session || !this.view || this.resultSaving) return;
      const key = resultSubmitKey(payload);
      // A retry of the same input reuses its commandId, so a double submit records it once.
      if (!this.pendingResult || this.pendingResult.key !== key) {
        this.pendingResult = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingResult;
      const checklistId = this.view.checklist.id;
      this.resultSaving = true;
      this.resultError = null;
      try {
        const view = await this.session.run((revision) =>
          recordWorkshopResult(checklistId, revision, commandId, payload),
        );
        this.pendingResult = null;
        this.view = view;
        this.selected = keepSelection(view, this.selected);
      } catch (err) {
        if (isWriteConflict(err)) {
          // revision_conflict sets the session's conflict; result_exists and stale_result do not.
          if (!isConflictError(err)) this.writeConflict = true;
        } else if (!(err && err.code === "conflict_pending")) {
          this.resultError = {
            taskCode: payload.taskCode,
            ...resultServerError(err),
          };
        }
      } finally {
        this.resultSaving = false;
      }
    },
    // Approved: open one task's read-only detail (a summary row, Precedente / Successiva).
    openDetail(code) {
      if (code) this.detail = code;
    },
    // "Accetta eccezione" on the task card: confirm with an optional note.
    openAccept(task) {
      const current = task && task.current;
      if (!current) return;
      this.decision = {
        ...this.decision,
        open: true,
        mode: "accept",
        resultId: current.id,
        taskCode: task.code,
        reason: current.reason || "",
        error: "",
      };
    },
    // "Approva bonifica": the dialog says "nei tempi" or "in ritardo".
    openApprove() {
      this.decision = {
        ...this.decision,
        open: true,
        mode: "approve",
        resultId: null,
        taskCode: null,
        reason: "",
        error: "",
      };
    },
    // Runs the confirmed acceptance or approval through the checklist session. The returned view
    // replaces the page's (an approval switches to the summary list). A 409 closes the dialog and
    // shows the conflict banner with "Ricarica"; anything else stays in the dialog.
    async decide(note) {
      const decision = this.decision;
      if (!this.session || !this.view || decision.saving) return;
      const checklistId = this.view.checklist.id;
      const { mode, resultId } = decision;
      const key = decisionKey(mode, { checklistId, resultId, note });
      // A retry of the same decision reuses its commandId, so a double click writes it once.
      if (!this.pendingDecision || this.pendingDecision.key !== key) {
        this.pendingDecision = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingDecision;
      decision.saving = true;
      decision.error = "";
      try {
        const view = await this.session.run((revision) =>
          mode === "accept"
            ? acceptWorkshopException(
                checklistId,
                resultId,
                revision,
                commandId,
                note,
              )
            : approveWorkshopChecklist(checklistId, revision, commandId),
        );
        this.pendingDecision = null;
        this.view = view;
        this.selected = keepSelection(view, this.selected);
        if (view.approval) this.detail = null;
        decision.open = false;
      } catch (err) {
        if (isWriteConflict(err)) {
          // revision_conflict sets the session's conflict; the other 409s do not.
          if (!isConflictError(err)) this.writeConflict = true;
          decision.open = false;
        } else if (err && err.code === "conflict_pending") {
          decision.open = false;
        } else {
          decision.error = decisionError(err);
        }
      } finally {
        decision.saving = false;
      }
    },
    // "Correggi" on a value of a task's current result (slice 9; the API checks the role).
    openCorrection({ task, field }) {
      const current = task && task.current;
      if (!current || !this.view || !this.view.canCorrect) return;
      this.previewSeq += 1;
      this.pendingCorrection = null;
      this.correction = {
        open: true,
        field,
        taskCode: task.code,
        resultId: current.id,
        saving: false,
        error: "",
        preview: null,
      };
    },
    onCorrectionToggle(open) {
      this.correction.open = open;
      if (!open) {
        // An answer to a dry run that is still on its way is ignored.
        this.previewSeq += 1;
        this.correction.preview = null;
      }
    },
    // A 409 of a correction closes the dialog and shows the conflict banner with "Ricarica".
    closeOnConflict(err) {
      if (isWriteConflict(err)) {
        // revision_conflict sets the session's conflict; result_superseded does not.
        if (!isConflictError(err)) this.writeConflict = true;
      } else if (!(err && err.code === "conflict_pending")) {
        return false;
      }
      this.correction.open = false;
      this.correction.preview = null;
      return true;
    },
    // The dry run of a correction: the change and its warnings, nothing saved. request is
    // { group, value } from the dialog, or null to clear the preview.
    async previewCorrection(request) {
      this.previewSeq += 1;
      const seq = this.previewSeq;
      const { correction } = this;
      if (!request || !this.session || !this.view) {
        correction.preview = null;
        return;
      }
      correction.preview = { loading: true, error: "", data: null };
      const checklistId = this.view.checklist.id;
      const { resultId, field } = correction;
      try {
        const data = await this.session.run((revision) =>
          correctWorkshopResult(checklistId, revision, newCommandId(), {
            resultId,
            field,
            value: request.value,
            dryRun: true,
          }),
        );
        if (seq === this.previewSeq) {
          correction.preview = { loading: false, error: "", data };
        }
      } catch (err) {
        if (seq !== this.previewSeq || this.closeOnConflict(err)) return;
        correction.preview = {
          loading: false,
          error: correctionMessage(err, (key) => this.$t(key)),
          data: null,
        };
      }
    },
    // Saves the correction; a retry of the same input reuses its commandId. The returned view
    // replaces the page's: the corrected value and "Valore precedente" show, an approval stays.
    async submitCorrection({ group, value, reason }) {
      const { correction } = this;
      if (!this.session || !this.view || correction.saving) return;
      const checklistId = this.view.checklist.id;
      const { resultId } = correction;
      const key = correctionSubmitKey({
        checklistId,
        resultId,
        field: group,
        value,
        reason,
      });
      if (!this.pendingCorrection || this.pendingCorrection.key !== key) {
        this.pendingCorrection = { key, commandId: newCommandId() };
      }
      const { commandId } = this.pendingCorrection;
      correction.saving = true;
      correction.error = "";
      try {
        const view = await this.session.run((revision) =>
          correctWorkshopResult(checklistId, revision, commandId, {
            resultId,
            field: group,
            value,
            reason,
          }),
        );
        this.pendingCorrection = null;
        this.view = view;
        this.selected = keepSelection(view, this.selected);
        correction.open = false;
      } catch (err) {
        if (!this.closeOnConflict(err)) {
          correction.error = this.$t("acceptance.corrections.failed", {
            message: correctionMessage(err, (k) => this.$t(k)),
          });
        }
      } finally {
        correction.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.workshop-checklist {
  max-width: 100%;
  overflow-x: hidden;
}

/* Desktop: the page fills the viewport and each column scrolls on its own. */
.workshop-checklist-desktop {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px));
}

/* Phone: at least the viewport high, so the bottom bar sits at the bottom even on a short task. */
.workshop-checklist-phone {
  display: flex;
  flex-direction: column;
  min-height: calc(
    100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px)
  );
}

.workshop-top {
  flex: 0 0 auto;
}

.workshop-columns {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  gap: 16px;
}

.workshop-column-list {
  flex: 0 0 33%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.workshop-column-main {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.workshop-column {
  min-height: 0;
  overflow-y: auto;
}

/* The panel follows the card; a long card scrolls and the panel stays visible under it. */
.workshop-column-main > .workshop-column {
  flex: 0 1 auto;
}

.workshop-approval {
  flex: 0 0 auto;
}

/* Approved, desktop: the summary column (the card has its own border). */
.workshop-column-summary {
  flex: 0 0 38%;
}

/* Approved, phone: the summary or the detail. */
.workshop-approved {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* Phone (and the approved detail): the footer stays inside the viewport at the bottom. */
.workshop-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: auto;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.workshop-footer-approval {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.workshop-bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 8px;
}

.workshop-bottom-bar .v-btn {
  min-height: 48px;
  min-width: 0;
  padding: 0 8px;
}

.workshop-wrap {
  overflow-wrap: anywhere;
}

.workshop-vin {
  font-family: monospace;
  word-break: break-all;
}

.workshop-link {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}
</style>
